import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  LayoutDashboard, PackageCheck, Truck, Users, Warehouse, Wrench,
  FileText, IndianRupee, BarChart3, UserCog, LogOut, Fuel, Menu, X,
  Home, Route as RouteIcon, Sun, Moon, Bell, ShieldCheck, Files, FileSignature, UserRound,
  UserPlus, Gavel, ShoppingCart, TestTube2, Users2, ScrollText, Map, Sparkles,
  Wallet, LifeBuoy, Settings, Landmark, Navigation, KeyRound, ClipboardCheck, Receipt,
  Clock, DatabaseBackup, Building2,
} from "lucide-react";

const NAV_GROUPS_ADMIN = [
  { title: "Operations", items: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin","sales","dispatcher","accounts","operator"] },
    { to: "/app/orders", label: "Orders", icon: PackageCheck, roles: ["admin","sales","dispatcher","accounts"] },
    { to: "/app/dispatch", label: "Dispatch", icon: Truck, roles: ["admin","dispatcher"] },
    { to: "/app/route-optimizer", label: "Route Optimizer", icon: Navigation, roles: ["admin","dispatcher"] },
    { to: "/app/tracking", label: "Live Tracking", icon: Map, roles: ["admin","dispatcher","sales"] },
    { to: "/app/ongoing-trips", label: "Ongoing Trips", icon: Navigation, roles: ["admin","dispatcher","sales","accounts"] },
    { to: "/app/ai", label: "AI Assistant", icon: Sparkles, roles: ["admin","sales","accounts"] },
  ]},
  { title: "Customers", items: [
    { to: "/app/customers", label: "Customers", icon: Users, roles: ["admin","sales","accounts"] },
    { to: "/app/leads", label: "Leads & CRM", icon: UserPlus, roles: ["admin","sales"] },
    { to: "/app/contracts", label: "Contracts", icon: FileSignature, roles: ["admin","sales","accounts"] },
    { to: "/app/tenders", label: "Tenders", icon: Gavel, roles: ["admin","sales"] },
    { to: "/app/support", label: "Support", icon: LifeBuoy, roles: ["admin","sales","accounts"] },
  ]},
  { title: "Fleet & Stock", items: [
    { to: "/app/inventory", label: "Inventory", icon: Warehouse, roles: ["admin","operator"] },
    { to: "/app/tanks", label: "Storage Tanks", icon: Warehouse, roles: ["admin","operator","accounts"] },
    { to: "/app/vendors", label: "Vendors", icon: Building2, roles: ["admin","accounts","operator"] },
    { to: "/app/procurement", label: "Procurement", icon: ShoppingCart, roles: ["admin","accounts","operator"] },
    { to: "/app/release-history", label: "Release History", icon: ScrollText, roles: ["admin","accounts","operator","manager"] },
    { to: "/app/quality", label: "Quality Tests", icon: TestTube2, roles: ["admin","operator","accounts"] },
    { to: "/app/fleet", label: "Fleet", icon: Wrench, roles: ["admin","dispatcher"] },
    { to: "/app/loadings", label: "Loadings", icon: Warehouse, roles: ["admin","operator","dispatcher","accounts"] },
    { to: "/app/loading-sources", label: "Loading Sources", icon: Warehouse, roles: ["admin","manager"] },
    { to: "/app/service-records", label: "Service Records", icon: Wrench, roles: ["admin","dispatcher","operator","accounts"] },
    { to: "/app/bowser-fuel", label: "Bowser Fuel Log", icon: Fuel, roles: ["admin","dispatcher","operator","accounts","sales"] },
    { to: "/app/compliance", label: "Compliance", icon: ShieldCheck, roles: ["admin","accounts"] },
    { to: "/app/documents", label: "Documents", icon: Files, roles: ["admin","accounts","sales"] },
    { to: "/app/challans", label: "Delivery Challans", icon: FileText, roles: ["admin","dispatcher","accounts","manager"] },
    { to: "/app/loading-invoices", label: "Loading Invoices", icon: FileText, roles: ["admin","accounts","manager"] },
    { to: "/app/stock-audit", label: "Stock Audit", icon: ClipboardCheck, roles: ["admin","accounts","manager"] },
    { to: "/app/expenses", label: "Trip Expenses", icon: Receipt, roles: ["admin","dispatcher","accounts","manager","driver"] },
  ]},
  { title: "Finance", items: [
    { to: "/app/invoices", label: "Invoices", icon: FileText, roles: ["admin","accounts","manager"] },
    { to: "/app/payments", label: "Payments", icon: IndianRupee, roles: ["admin","accounts","manager"] },
    { to: "/app/products", label: "Products & GST", icon: Fuel, roles: ["admin","accounts","manager"] },
    { to: "/app/finance", label: "Accounts (Journal · TB · P&L)", icon: Landmark, roles: ["admin","accounts","manager"] },
    { to: "/app/finance-bi", label: "Analytics BI", icon: BarChart3, roles: ["admin","accounts","manager"] },
    { to: "/app/hr", label: "HR & Payroll", icon: Users2, roles: ["admin","accounts","manager"] },
    { to: "/app/reports", label: "Reports", icon: BarChart3, roles: ["admin","accounts","manager"] },
  ]},
  { title: "Admin", items: [
    { to: "/app/users", label: "Users", icon: UserCog, roles: ["admin", "accounts"] },
    { to: "/app/audit", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
    { to: "/app/integrations", label: "Integrations", icon: KeyRound, roles: ["admin"] },
    { to: "/app/cms", label: "Landing CMS", icon: Landmark, roles: ["admin"] },
    { to: "/app/settings", label: "Settings", icon: Settings, roles: ["admin"] },
  ]},
];

const CUSTOMER_NAV = [
  { to: "/portal/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/portal/book", label: "Book Diesel", icon: Fuel },
  { to: "/portal/invoices", label: "Invoices", icon: FileText },
  { to: "/portal/challans", label: "Delivery Challans", icon: FileText },
  { to: "/portal/wallet", label: "Wallet", icon: Wallet },
  { to: "/portal/support", label: "Support", icon: LifeBuoy },
];

const DRIVER_NAV = [
  { to: "/driver", label: "My Trips", icon: RouteIcon, end: true },
  { to: "/driver/loadings", label: "Loadings", icon: Warehouse },
  { to: "/driver/attendance", label: "Attendance", icon: Clock },
  { to: "/driver/self-fuel", label: "Self Fueling", icon: Fuel },
];

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return localStorage.getItem("ddd_theme") === "dark"; } catch { return false; }
  });
  useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    try { localStorage.setItem("ddd_theme", dark ? "dark" : "light"); } catch { /* noop */ }
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
}

function NotificationBell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications?limit=20").then((r) => r.data),
    refetchInterval: 30000,
  });
  const markAll = useMutation({
    mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markOne = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const unread = data?.unread_count || 0;

  const handleClick = (n) => {
    if (!n.read) markOne.mutate(n.id);
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative rounded-full border border-border/60 p-2 hover:border-primary/50" data-testid="btn-notifications">
          <Bell size={16} />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-2">
          <div className="text-sm font-semibold">Notifications</div>
          <button
            onClick={() => markAll.mutate()}
            className="text-xs text-primary hover:underline"
            data-testid="btn-mark-all-read"
          >Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {(data?.items || []).length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No notifications yet</div>
          )}
          {(data?.items || []).map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={cn(
                "flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left hover:bg-muted/40",
                !n.read && "bg-primary/5",
              )}
              data-testid={`notification-${n.id}`}
            >
              <span className={cn(
                "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                n.kind === "urgent" ? "bg-status-cancelled" :
                n.kind === "warning" ? "bg-status-pending" :
                n.kind === "success" ? "bg-status-delivered" : "bg-primary",
              )} />
              <div className="flex-1">
                <div className="text-sm font-semibold">{n.title}</div>
                <div className="text-xs text-muted-foreground">{n.body}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AppShell({ variant = "admin" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, toggleDark] = useDarkMode();

  // Fetch CMS brand (public endpoint) so the logo + company name propagate
  // everywhere the app is rendered (admin ERP / customer portal / driver app).
  // Uses localStorage as initial data to prevent brand/logo flash on page refresh
  // — the query still refetches to catch any admin edits.
  const CMS_CACHE_KEY = "fuelexpress_cms_cache_v1";
  const { data: cms } = useQuery({
    queryKey: ["cms-landing"],
    queryFn: async () => {
      const r = await api.get("/cms/landing");
      try { localStorage.setItem(CMS_CACHE_KEY, JSON.stringify(r.data)); } catch { /* noop */ }
      return r.data;
    },
    staleTime: 60_000,
    initialData: () => {
      try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null") || undefined; }
      catch { return undefined; }
    },
  });
  const brandCfg = (cms?.content || cms)?.brand || {};
  const masterName = brandCfg.name || "Diesel·Dash";
  const masterLogo = brandCfg.logo_url || "";

  // Driver-only: poll for open loading tasks so the nav item + badge stay
  // in sync as admin assigns / approves. Zero requests for non-driver views.
  const { data: myLoadings } = useQuery({
    queryKey: ["driver-loadings-nav"],
    queryFn: () => api.get("/loadings/mine").then((r) => r.data.items || []),
    enabled: variant === "driver" && !!user,
    refetchInterval: 30000,
  });
  const driverLoadingCount = (myLoadings || []).length;

  // Per-panel overrides — customer / admin / driver each can set their own
  // display name, tagline and logo in the CMS. Fall back to the master brand.
  const perVariant = variant === "customer"
    ? {
        name: brandCfg.customer_panel_name || masterName,
        logo: brandCfg.customer_panel_logo_url || masterLogo,
        tag: brandCfg.customer_panel_tagline || "Customer Portal",
      }
    : variant === "driver"
      ? {
          name: brandCfg.driver_panel_name || masterName,
          logo: brandCfg.driver_panel_logo_url || masterLogo,
          tag: brandCfg.driver_panel_tagline || "Driver App",
        }
      : {
          name: brandCfg.admin_panel_name || masterName,
          logo: brandCfg.admin_panel_logo_url || masterLogo,
          tag: brandCfg.admin_panel_tagline || "Operations ERP",
        };
  const { name: brandName, logo: logoUrl, tag: brandTag } = perVariant;

  const brand = (
    <div className="flex items-center gap-2.5" data-testid="app-brand">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={brandName}
          className="h-10 w-10 rounded-md object-contain bg-white p-0.5 ring-1 ring-border/60"
        />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Fuel size={18} strokeWidth={2.5} />
        </div>
      )}
      <div>
        <div className="font-display text-sm font-bold leading-tight" data-testid="app-brand-name">{brandName}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="app-brand-tagline">
          {brandTag}
        </div>
      </div>
    </div>
  );

  const renderNav = () => {
    if (variant === "customer") return renderFlat(CUSTOMER_NAV);
    if (variant === "driver") {
      // Loadings nav item is conditional — only visible when the driver has
      // at least one assigned/submitted (not-yet-approved) loading. Hiding
      // it post-approval keeps the mobile nav uncluttered on quiet days.
      const items = DRIVER_NAV.filter((n) => n.to !== "/driver/loadings" || (driverLoadingCount || 0) > 0);
      return renderFlat(items);
    }
    return NAV_GROUPS_ADMIN.map((group) => {
      const items = group.items.filter((n) => !user || n.roles.includes(user.role));
      if (items.length === 0) return null;
      return (
        <div key={group.title} className="mb-2">
          <div className="mb-1 px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {group.title}
          </div>
          {renderFlat(items)}
        </div>
      );
    });
  };

  const renderFlat = (items) => items.map((it) => (
    <NavLink
      key={it.to}
      to={it.to}
      end={it.end}
      onClick={() => setOpen(false)}
      data-testid={`nav-${it.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
    >
      <it.icon size={16} strokeWidth={2} />
      {it.label}
    </NavLink>
  ));

  return (
    <div className="flex min-h-screen bg-background">
      {/* Backdrop on mobile — tap anywhere off-sidebar to close */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex h-[100dvh] w-64 flex-col overflow-hidden border-r border-border/60 bg-card transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
        data-testid="sidebar"
      >
        <div className="flex h-16 flex-shrink-0 items-center justify-between border-b border-border/60 bg-card px-5">
          {brand}
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={20} />
          </button>
        </div>
        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
          {renderNav()}
        </nav>
        <div className="relative z-10 flex-shrink-0 border-t border-border/60 bg-card p-3 shadow-[0_-6px_10px_-6px_rgba(0,0,0,0.15)]">
          <div className="mb-2 rounded-md border border-border/60 p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Signed in as</div>
            <div className="text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.email}</div>
            <div className="mt-1 inline-block rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
              {user?.role}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setOpen(false);
                nav(variant === "customer" ? "/portal/profile" : variant === "driver" ? "/driver/profile" : "/app/profile");
              }}
              data-testid="btn-my-profile"
            >
              <UserRound size={14} className="mr-1" /> My profile
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={toggleDark} data-testid="theme-toggle">
                {dark ? <Sun size={14} /> : <Moon size={14} />}
                <span className="ml-1">{dark ? "Light" : "Dark"}</span>
              </Button>
              <Button variant="outline" size="sm" className="flex-1" onClick={async () => { setOpen(false); await logout(); nav("/login"); }} data-testid="btn-logout">
                <LogOut size={14} className="mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border/60 bg-background/85 px-4 backdrop-blur lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="sidebar-toggle">
              <Menu size={20} />
            </button>
            <div className="hidden lg:block">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {loc.pathname.split("/").filter(Boolean).join(" / ")}
              </div>
              <div className="font-display text-lg font-bold">
                {loc.pathname.split("/").pop().replace(/-/g, " ") || "Overview"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <NavLink to="/" className="hidden text-xs text-muted-foreground hover:text-foreground lg:inline-flex items-center gap-1" data-testid="link-home">
              <Home size={14} /> Public
            </NavLink>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8 animate-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
